import { useState } from "react";
import { toast } from "sonner";
import { Search, Instagram, Info, Hash, AtSign, SlidersHorizontal, CreditCard } from "lucide-react";
import { Button, Input, Card } from "@/components/kit";
import CityCombobox from "@/components/CityCombobox";
import ScrapeResults from "@/components/ScrapeResults";
import ScrapeHistory from "@/components/ScrapeHistory";
import useScrape from "@/lib/useScrape";

export default function InstagramScraper() {
  const [keyword, setKeyword] = useState("");
  const [city, setCity] = useState("Jakarta");
  const [count, setCount] = useState(20);
  const [onlyPhone, setOnlyPhone] = useState(false);
  const { results, setResults, selected, loading, saving, run, toggle, save, selectedLeads, suggestions, progress } = useScrape("instagram");

  const onRun = () => {
    if (!keyword.trim()) { toast.error("Masukkan keyword / hashtag / username"); return; }
    run({ keyword: keyword.replace(/^[@#]/, ""), city, count, only_phone: onlyPhone });
  };

  const onRerun = (h) => {
    setKeyword(h.keyword);
    setCity(h.city);
    run({ keyword: h.keyword, city: h.city, count, only_phone: onlyPhone });
  };

  return (
    <div className="space-y-5 animate-float-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl grid place-items-center shadow-sm"
            style={{ background: "linear-gradient(135deg, rgba(253,121,17,0.1), rgba(214,41,118,0.1), rgba(150,47,191,0.1))" }}>
            <Instagram className="w-5.5 h-5.5" style={{ color: "#d62976" }} />
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Scraper Instagram</h1>
            <p className="text-slate-400 mt-0.5 text-xs">Temukan akun bisnis Instagram + kontak dari bio.</p>
          </div>
        </div>
      </div>

      {/* Info banner */}
      <Card className="p-3.5 border-pink-200/60" style={{ background: "linear-gradient(135deg, rgba(253,121,17,0.03), rgba(214,41,118,0.03), rgba(150,47,191,0.03))" }}>
        <p className="text-xs text-pink-600 flex items-center gap-2">
          <Info className="w-3.5 h-3.5 shrink-0" />
          Cari akun bisnis lewat keyword,
          <span className="inline-flex items-center gap-0.5 font-bold bg-pink-50 px-1 py-0.5 rounded"><Hash className="w-3 h-3" />hashtag</span>,
          atau <span className="inline-flex items-center gap-0.5 font-bold bg-pink-50 px-1 py-0.5 rounded"><AtSign className="w-3 h-3" />username</span>.
          Setiap hasil memakai <span className="font-bold bg-pink-50 px-1.5 py-0.5 rounded">2 kredit</span>.
        </p>
      </Card>

      {/* Form */}
      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-4 items-start">
        <div className="space-y-3 order-2 xl:order-1">
          {/* Search */}
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <Search className="w-3.5 h-3.5" /> Pencarian
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Keyword / Hashtag / Username</label>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="scrape-keyword" placeholder="#kulinerjakarta, fashion, @brandname..." value={keyword}
                  onChange={(e) => setKeyword(e.target.value)} className="pl-9 h-10 text-sm rounded-xl"
                  onKeyDown={(e) => e.key === "Enter" && onRun()} />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Kota / Lokasi</label>
              <button
                onClick={() => setCity("")}
                className={`text-[11px] font-semibold mb-1.5 px-3 py-1 rounded-lg border transition ${!city ? "bg-pink-500 text-white border-pink-500" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
              >
                🇮🇩 Semua Indonesia
              </button>
              {city && <CityCombobox value={city} onSelect={(c) => setCity(c.name || "")} />}
              {!city && <p className="text-[10px] text-pink-500 font-medium mt-1">Semua kota di Indonesia</p>}
            </div>
            <Button data-testid="scrape-run" onClick={onRun} loading={loading} size="lg"
              className="w-full h-11 rounded-xl text-white font-semibold shadow-lg transition-all duration-200"
              style={{ background: "linear-gradient(135deg, #fa7e1e, #d62976, #962fbf)", boxShadow: "0 8px 24px -8px rgba(214,41,118,0.4)" }}>
              <Search className="w-4 h-4" /> Cari Akun
            </Button>
          </Card>

          {/* Parameters */}
          <Card className="p-4 space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Parameter
            </div>
            <div>
              <label className="flex justify-between text-[11px] font-semibold text-slate-400 mb-1.5">
                <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> JUMLAH HASIL</span>
                <span className="text-pink-500 font-bold">{count}</span>
              </label>
              <input type="range" min="5" max="100" step="5" value={count} onChange={(e) => setCount(Number(e.target.value))}
                className="w-full h-1.5 rounded-full" />
            </div>
            <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer group">
              <input type="checkbox" checked={onlyPhone} onChange={(e) => setOnlyPhone(e.target.checked)} />
              <span className="group-hover:text-slate-800 transition text-[13px]">Hanya yang punya nomor telepon</span>
            </label>
          </Card>

          <div className="flex items-center justify-center gap-2 text-[11px] text-slate-400 py-1">
            <CreditCard className="w-3 h-3" />
            Estimasi: <span className="font-bold text-pink-500">{count * 2}</span> kredit
          </div>

          <ScrapeHistory source="instagram" onRerun={onRerun} />
        </div>

        {/* Results */}
        <div className="order-1 xl:order-2">
          <ScrapeResults source="instagram" results={results} setResults={setResults} loading={loading} selected={selected} toggle={toggle}
            selectedLeads={selectedLeads} save={save} saving={saving} progress={progress} suggestions={suggestions}
            emptyHint="Hasil pencarian akun Instagram akan tampil di sini." />
        </div>
      </div>
    </div>
  );
}
